import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();
    this.default_quantity_selected = 1;
    this.quantity_break_items = [];
    this.selectorsZone = null;
    this.itemClickHandlers = new Map();

    this.handleSubscriptionUpdate = this.#onSubscriptionUpdated.bind(this);
    this.handleVariantUpdated = this.onVariantUpdated.bind(this);
  }

  connectedCallback() {
    if (this.dataset.qbInitialized === 'true') return;
    this.dataset.qbInitialized = 'true';

    this.default_quantity_selected = parseInt(this.dataset.quantitySelectedByDefault, 10) || 1;
    this.quantity_break_items = Array.from(this.querySelectorAll('[data-ref="quantity-break-item"]'));
    this.selectorsZone = this.querySelector('[data-ref="qb-selectors-zone"]');

    this.#moveSelectorsIntoZone();
    this.#bindQuantityCards();
    this.updateDefaultQuantitySelected();
    this.#syncSelectors();

    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdate);

    this.itemClickHandlers.forEach((handlers, item) => {
      item.removeEventListener('click', handlers.click);
      item.removeEventListener('keydown', handlers.keydown);
    });
    this.itemClickHandlers.clear();

    delete this.dataset.qbInitialized;
  }

  #bindQuantityCards() {
    this.quantity_break_items.forEach((quantityBreak) => {
      const selectItem = () => {
        if (quantityBreak.classList.contains('quantity-break--selected')) return;
        if (quantityBreak.classList.contains('quantity-break--disabled')) return;
        this.selectQuantity(quantityBreak);
      };

      const click = () => selectItem();
      const keydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectItem();
      };

      quantityBreak.addEventListener('click', click);
      quantityBreak.addEventListener('keydown', keydown);
      this.itemClickHandlers.set(quantityBreak, { click, keydown });
    });
  }

  #moveSelectorsIntoZone() {
    if (!this.selectorsZone) return;

    this.querySelectorAll('.qb-selectors').forEach((selectorGroup) => {
      this.selectorsZone.appendChild(selectorGroup);

      selectorGroup.querySelectorAll('select').forEach((select) => {
        if (select.dataset.qbListenerBound === 'true') return;
        select.dataset.qbListenerBound = 'true';

        select.addEventListener('change', () => {
          this.#emitPublicUpdate();
        });
      });
    });
  }

  updateDefaultQuantitySelected() {
    this.resetSelectedQuantity();

    const preferredItem = this.quantity_break_items.find(
      (item) => parseInt(item.dataset.quantity, 10) === this.default_quantity_selected && !item.isDisabled?.(),
    );

    const fallbackItem = this.quantity_break_items.find(
      (item) => !item.classList.contains('quantity-break--disabled'),
    );

    const itemToSelect = preferredItem || fallbackItem || this.quantity_break_items[0];
    itemToSelect?.classList.add('quantity-break--selected');
    this.#syncSelectionAccessibility();
  }

  selectQuantity(quantityBreak) {
    this.resetSelectedQuantity();
    this.resetCrossSellCheckboxes();
    quantityBreak.classList.add('quantity-break--selected');
    this.#syncSelectionAccessibility();

    this.#syncSelectors();
    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    this.#emitPublicUpdate();
  }

  #emitPublicUpdate() {
    document.dispatchEvent(
      new CustomEvent('quantity-breaks:updated', {
        detail: {
          quantityBreaks: this,
          quantity: parseInt(this.getSelectedQuantity()?.dataset.quantity, 10) || 1,
        },
      }),
    );
  }

  resetCrossSellCheckboxes() {
    this.querySelectorAll('[data-ref="quantity-break-cross-sell-checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  resetSelectedQuantity() {
    this.quantity_break_items.forEach((quantityBreak) => {
      quantityBreak.classList.remove('quantity-break--selected');
    });
  }

  #syncSelectionAccessibility() {
    this.quantity_break_items.forEach((item) => {
      const selected = item.classList.contains('quantity-break--selected');
      const disabled = item.classList.contains('quantity-break--disabled');
      item.setAttribute('aria-checked', selected ? 'true' : 'false');
      item.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      item.tabIndex = selected && !disabled ? 0 : -1;
    });
  }

  getSelectedQuantity() {
    return this.querySelector('.quantity-break--selected');
  }

  getPrices() {
    return this.getSelectedQuantity()?.getPrices?.() || { price: 0, compareAtPrice: 0 };
  }

  getCrossSellPrices() {
    return this.getSelectedQuantity()?.getCrossSellPrices?.() || { price: 0, compareAtPrice: 0 };
  }

  getDiscount() {
    return this.getSelectedQuantity()?.getDiscount?.() || { type: 'none', value: 0, percentage: 0 };
  }

  getSelectedMainItems() {
    const items = [];
    const selectedBreak = this.getSelectedQuantity();
    if (!selectedBreak) return items;

    const selectedQty = selectedBreak.dataset.quantity;
    const activeSelector = this.selectorsZone?.querySelector(
      `.qb-selectors[data-for-quantity="${selectedQty}"]`,
    );
    const externalPickers = activeSelector?.querySelectorAll(
      '[data-ref="quantity-break-variant-picker"]',
    );

    if (externalPickers?.length) {
      externalPickers.forEach((picker) => {
        items.push({ id: picker.value, quantity: 1 });
      });
    } else if (this.isVariantSelectorActivated() && this.hasVariants()) {
      selectedBreak
        .querySelectorAll('[data-ref="quantity-break-variant-picker"]')
        .forEach((variantPicker) => {
          items.push({ id: variantPicker.value, quantity: 1 });
        });
    } else {
      items.push({
        id: selectedBreak.dataset.variantId,
        quantity: parseInt(selectedBreak.dataset.quantity, 10) || 1,
      });
    }

    return items;
  }

  getSelectedItems() {
    const selectedBreak = this.getSelectedQuantity();
    if (!selectedBreak) return [];

    const mainItems = this.getSelectedMainItems();
    const crossSellItems = selectedBreak.getCrossSellItems?.() || [];
    return [...mainItems, ...crossSellItems];
  }

  getSelectedConfigurations() {
    const selectedBreak = this.getSelectedQuantity();
    if (!selectedBreak) return [];

    const quantity = parseInt(selectedBreak.dataset.quantity, 10) || 1;
    const activeSelector = this.selectorsZone?.querySelector(
      `.qb-selectors[data-for-quantity="${quantity}"]`,
    );
    const pickers = activeSelector?.querySelectorAll('[data-ref="quantity-break-variant-picker"]');

    if (pickers?.length) {
      return Array.from(pickers).map((picker, index) => {
        const option = picker.options[picker.selectedIndex];
        return {
          index: index + 1,
          id: picker.value,
          title: option?.textContent?.trim() || this.dataset.defaultVariantTitle || '',
        };
      });
    }

    return Array.from({ length: quantity }, (_, index) => ({
      index: index + 1,
      id: selectedBreak.dataset.variantId,
      title: this.dataset.defaultVariantTitle || '',
    }));
  }

  isVariantSelectorActivated() {
    return this.dataset.showVariantPicker === 'true';
  }

  hasVariants() {
    return this.dataset.hasVariants === 'true';
  }

  onVariantUpdated(event) {
    const currentQuantityBreaks = this;
    let newQuantityBreaks = currentQuantityBreaks;

    if (event?.detail?.data?.html) {
      newQuantityBreaks = event.detail.data.html.querySelector('[data-ref="quantity-breaks"]');
    }

    if (!newQuantityBreaks || !currentQuantityBreaks) return;

    if (currentQuantityBreaks !== newQuantityBreaks) {
      const newItems = newQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]');
      currentQuantityBreaks
        .querySelectorAll('[data-ref="quantity-break-item"]')
        .forEach((quantityBreak, index) => {
          if (newItems[index]) quantityBreak.render(newItems[index]);
        });

      this.#handleDisabledSelectedItem();
      this.#syncSelectors();
      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      this.#emitPublicUpdate();
    }
  }

  isActive() {
    return !this.classList.contains('quantity-breaks--hidden');
  }

  #shouldBeActive() {
    const appliesTo = this.dataset.appliesTo || 'both';
    if (appliesTo === 'both') return true;

    const subscription = this.closest('[data-ref="product-form"]')?.querySelector('[data-ref="subscription"]');
    if (!subscription || typeof subscription.isSubscriptionSelected !== 'function') return true;

    const isSubscriptionSelected = subscription.isSubscriptionSelected();
    if (appliesTo === 'one_time') return !isSubscriptionSelected;
    if (appliesTo === 'subscription') return isSubscriptionSelected;
    return true;
  }

  #onSubscriptionUpdated() {
    const wasActive = this.isActive();
    const shouldBeActive = this.#shouldBeActive();

    if (wasActive === shouldBeActive) return;

    this.classList.toggle('quantity-breaks--hidden', !shouldBeActive);

    if (!shouldBeActive) {
      this.updateDefaultQuantitySelected();
      this.resetCrossSellCheckboxes();
    }

    this.#syncSelectors();
    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    this.#emitPublicUpdate();
  }

  #handleDisabledSelectedItem() {
    const selectedItem = this.getSelectedQuantity();
    if (!selectedItem || !selectedItem.isDisabled?.()) return;

    const firstAvailableItem = this.quantity_break_items.find(
      (item) => !item.classList.contains('quantity-break--disabled'),
    );

    if (firstAvailableItem) {
      this.resetSelectedQuantity();
      this.resetCrossSellCheckboxes();
      firstAvailableItem.classList.add('quantity-break--selected');
      this.#syncSelectionAccessibility();
    }
  }

  #syncSelectors() {
    if (!this.selectorsZone) return;

    const selectedQty = this.getSelectedQuantity()?.dataset.quantity || '1';
    this.selectorsZone.dataset.activeQuantity = selectedQty;

    this.selectorsZone.querySelectorAll('.qb-selectors').forEach((selectorGroup) => {
      const isActive = selectorGroup.dataset.forQuantity === selectedQty;
      selectorGroup.hidden = !isActive;
      selectorGroup.classList.toggle('qb-selectors--active', isActive);
      selectorGroup.style.display = isActive ? 'grid' : 'none';

      selectorGroup.querySelectorAll('select, input, textarea').forEach((field) => {
        field.disabled = !isActive;
      });
    });
  }

}

if (!customElements.get('quantity-breaks')) {
  customElements.define('quantity-breaks', QuantityBreaks);
}

class QuantityBreakItem extends HTMLElement {
  constructor() {
    super();
    this.variant_pickers = [];
    this.variant_thumbnails = [];
    this.price = null;
    this.cross_sells = [];
  }

  connectedCallback() {
    if (this.dataset.qbItemInitialized === 'true') return;
    this.dataset.qbItemInitialized = 'true';

    this.variant_pickers = Array.from(this.querySelectorAll('[data-ref="quantity-break-variant-picker"]'));
    this.variant_thumbnails = Array.from(this.querySelectorAll('[data-ref="quantity-break-variant-thumbnail"]'));
    this.price = this.querySelector('[data-ref="quantity-break-price"]');
    this.cross_sells = Array.from(this.querySelectorAll('[data-ref="quantity-break-cross-sell"]'));

    this.variant_pickers.forEach((variantPicker, index) => {
      variantPicker.addEventListener('change', () => {
        this.#updateTotalPrice();
        this.#updateVariantThumbnail(variantPicker, index);
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });

    this.cross_sells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      const checkboxLabel = checkbox?.closest('.checkbox');

      crossSell.addEventListener('click', (event) => {
        event.stopPropagation();
        if (checkbox && !checkboxLabel?.contains(event.target)) {
          checkbox.checked = !checkbox.checked;
          this.dispatchEvent(new QuantityBreaksUpdatedEvent());
        }
      });

      checkbox?.addEventListener('change', () => {
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });
  }

  #updateVariantThumbnail(variantPicker, index) {
    const thumbnailContainer = this.variant_thumbnails[index];
    if (!thumbnailContainer) return;

    const selectedOption = variantPicker.options[variantPicker.selectedIndex];
    const variantImageUrl = selectedOption.dataset.variantImage;
    const variantImageAlt = selectedOption.dataset.variantImageAlt || '';

    if (variantImageUrl) {
      thumbnailContainer.classList.remove('quantity-break__variant-thumbnail--hidden');
      thumbnailContainer.innerHTML = `<img src="${variantImageUrl}" alt="${variantImageAlt}" width="40" height="40" loading="lazy">`;
    } else {
      thumbnailContainer.classList.add('quantity-break__variant-thumbnail--hidden');
      thumbnailContainer.innerHTML = '';
    }
  }

  #updateTotalPrice() {
    if (!this.price) return;

    let baseTotalPrice = 0;
    let catalogCompareAtPrice = 0;

    this.variant_pickers.forEach((variantPicker) => {
      const selectedOption = variantPicker.options[variantPicker.selectedIndex];
      const optionPrice = parseInt(selectedOption.dataset.price, 10) || 0;
      baseTotalPrice += optionPrice;
      catalogCompareAtPrice += parseInt(selectedOption.dataset.compareAtPrice, 10) || optionPrice;
    });

    const discountType = this.dataset.discountType;
    const discountValue = parseInt(this.dataset.discountValue, 10) || 0;
    const discountPercentage = parseFloat(this.dataset.discountPercentage) || 0;

    let totalDiscountValue = discountValue;
    if (discountType === 'percentage') {
      totalDiscountValue = Math.round((baseTotalPrice * discountPercentage) / 100);
    }

    const totalPrice = Math.max(0, baseTotalPrice - totalDiscountValue);
    const totalCompareAtPrice = this.dataset.savingReference === 'compare_at'
      ? Math.max(baseTotalPrice, catalogCompareAtPrice)
      : baseTotalPrice;
    const totalSaving = Math.max(0, totalCompareAtPrice - totalPrice);

    const priceNode = this.price.querySelector('[data-ref="price"]');
    const unitPriceNode = this.price.querySelector('[data-ref="unit-price"]');
    const compareNode = this.price.querySelector('[data-ref="compare-at-price"]');
    if (priceNode) priceNode.innerHTML = formatMoney(totalPrice);
    if (unitPriceNode) {
      const quantity = Math.max(1, parseInt(this.dataset.quantity, 10) || 1);
      const unitPriceText = this.dataset.unitPriceText || '/ maillot';
      unitPriceNode.textContent = `${formatMoney(Math.round(totalPrice / quantity))} ${unitPriceText}`;
    }
    if (compareNode) {
      const showCompare = totalCompareAtPrice > totalPrice;
      compareNode.hidden = !showCompare;
      compareNode.innerHTML = showCompare ? formatMoney(totalCompareAtPrice) : '';
    }

    this.dataset.totalPrice = String(totalPrice);
    this.dataset.totalCompareAtPrice = String(totalCompareAtPrice);

    const savingNode = this.querySelector('[data-ref="quantity-break-saving"]');
    if (savingNode) {
      savingNode.hidden = totalSaving <= 0;
      savingNode.textContent = `${this.dataset.savingLabel || 'Économisez'} ${formatMoney(totalSaving)}`;
    }
  }

  render(newQuantityBreak) {
    if (!newQuantityBreak) return;

    this.dataset.totalPrice = newQuantityBreak.dataset.totalPrice;
    this.dataset.totalCompareAtPrice = newQuantityBreak.dataset.totalCompareAtPrice;
    this.dataset.discountTotalValue = newQuantityBreak.dataset.discountTotalValue;
    this.dataset.variantId = newQuantityBreak.dataset.variantId;
    this.dataset.savingReference = newQuantityBreak.dataset.savingReference || 'current';
    this.dataset.savingLabel = newQuantityBreak.dataset.savingLabel || 'Économisez';

    const currentPrice = this.price?.querySelector('[data-ref="price"]');
    const currentCompare = this.price?.querySelector('[data-ref="compare-at-price"]');
    const newPrice = newQuantityBreak.querySelector('[data-ref="price"]');
    const newCompare = newQuantityBreak.querySelector('[data-ref="compare-at-price"]');

    if (currentPrice && newPrice) currentPrice.innerHTML = newPrice.innerHTML;
    if (currentCompare && newCompare) currentCompare.innerHTML = newCompare.innerHTML;

    const currentSaving = this.querySelector('[data-ref="quantity-break-saving"]');
    const newSaving = newQuantityBreak.querySelector('[data-ref="quantity-break-saving"]');
    if (currentSaving && newSaving) {
      currentSaving.hidden = newSaving.hidden;
      currentSaving.textContent = newSaving.textContent;
    }

    const currentImage = this.querySelector('[data-ref="quantity-break-image"]');
    const newImage = newQuantityBreak.querySelector('[data-ref="quantity-break-image"]');
    if (currentImage && newImage) currentImage.src = newImage.src;

    this.classList.toggle(
      'quantity-break--disabled',
      newQuantityBreak.classList.contains('quantity-break--disabled'),
    );
  }

  isDisabled() {
    return this.classList.contains('quantity-break--disabled');
  }

  getPrices() {
    return {
      price: parseInt(this.dataset.totalPrice, 10) || 0,
      compareAtPrice: parseInt(this.dataset.totalCompareAtPrice, 10) || 0,
    };
  }

  getCrossSellPrices() {
    return this.#getCheckedCrossSellPrices();
  }

  getCrossSellItems() {
    const items = [];
    this.querySelectorAll('[data-ref="quantity-break-cross-sell"]').forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        items.push({ id: crossSell.dataset.variantId, quantity: 1 });
      }
    });
    return items;
  }

  #getCheckedCrossSellPrices() {
    let price = 0;
    let compareAtPrice = 0;

    this.querySelectorAll('[data-ref="quantity-break-cross-sell"]').forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        const itemPrice = parseInt(crossSell.dataset.price, 10) || 0;
        const itemCompareAtPrice = parseInt(crossSell.dataset.compareAtPrice, 10) || itemPrice;
        price += itemPrice;
        compareAtPrice += itemCompareAtPrice;
      }
    });

    return { price, compareAtPrice };
  }

  getDiscount() {
    return {
      type: this.dataset.discountType || 'percentage',
      value: parseInt(this.dataset.discountValue, 10) || 0,
      percentage: parseFloat(this.dataset.discountPercentage) || 0,
    };
  }
}

if (!customElements.get('quantity-break-item')) {
  customElements.define('quantity-break-item', QuantityBreakItem);
}
